import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { BRAND } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState({ phase: "pending", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.error(e);
        setState({ phase: "error", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <>
      <Header />
      <section className="min-h-screen pt-40 pb-24" data-testid="success-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          {state.phase === "checking" && (
            <div data-testid="success-checking">
              <Loader2 className="h-10 w-10 text-gold mx-auto animate-spin mb-8" />
              <div className="overline mb-4">One moment</div>
              <h1 className="font-serif-display text-4xl text-ivory leading-tight">Confirming your reservation…</h1>
            </div>
          )}
          {state.phase === "paid" && (
            <div data-testid="success-paid">
              <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-8" />
              <div className="overline mb-4">Reservation confirmed</div>
              <h1 className="font-serif-display text-5xl text-ivory leading-tight mb-6">Thank you. We have it from here.</h1>
              <p className="text-ivory-muted leading-relaxed mb-2">
                A confirmation has been sent to your email. Your chauffeur will reach out before pickup.
              </p>
              {state.data && (
                <p className="text-gold text-sm tracking-widest mt-6">PAID · ${state.data.amount_total.toFixed(2)} {state.data.currency.toUpperCase()}</p>
              )}
              <div className="flex gap-4 justify-center mt-12">
                <Link to="/" className="btn-ghost-gold px-7 py-3" data-testid="success-home">Return Home</Link>
                <a href={`tel:${BRAND.phone}`} className="btn-gold px-7 py-3" data-testid="success-call">Call Us</a>
              </div>
            </div>
          )}
          {state.phase === "pending" && (
            <div data-testid="success-pending">
              <Loader2 className="h-10 w-10 text-gold mx-auto mb-8" />
              <h1 className="font-serif-display text-4xl text-ivory mb-4">Still processing…</h1>
              <p className="text-ivory-muted">Your payment is being confirmed. We've emailed your receipt as soon as it clears.</p>
              <Link to="/" className="btn-ghost-gold px-7 py-3 mt-8 inline-block">Return Home</Link>
            </div>
          )}
          {(state.phase === "expired" || state.phase === "error") && (
            <div data-testid="success-error">
              <XCircle className="h-12 w-12 text-gold mx-auto mb-8" />
              <h1 className="font-serif-display text-4xl text-ivory mb-4">Session expired.</h1>
              <p className="text-ivory-muted mb-8">Your checkout session is no longer active. Please start a new reservation.</p>
              <Link to="/book" className="btn-gold px-7 py-3">Book Again</Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
